import React from 'react';
import type { monaco } from 'react-monaco-editor';
import { EditorLineError, LineChanges } from '../EditorV3';

interface UseEditorDecorationsProps {
  editor?: monaco.editor.IStandaloneCodeEditor;
  lineErrors?: EditorLineError[];
  lineChanges?: LineChanges;
}

type DeltaDecoration = monaco.editor.IModelDeltaDecoration;
type LineRangeFunc = (startLine: number, endLine?: number) => monaco.IRange;
type DecorationFunc = (acc: DeltaDecoration[], current: EditorLineError) => DeltaDecoration[];
/**
 * Build a valid range for highlighting
 * a specific line inside the editor
 */
const buildInlineRange: LineRangeFunc = line => {
  return {
    startLineNumber: line,
    startColumn: 1,
    endLineNumber: line + 1,
    endColumn: 1,
  };
};

/**
 * Build a valid range for highlighting
 * a specific line on the left side of the editor
 */
const buildLineRange: LineRangeFunc = (startLine, endLine = startLine) => {
  return {
    startLineNumber: startLine,
    startColumn: 1,
    endLineNumber: endLine,
    endColumn: 1,
  };
};

/*
 * A helper function to build editor decoration for a specific line
 * Some helpful hints:
 * Glyph: refers to the icon before line number
 * Line: refers to space after the line number and before the editor body
 * Inline: refers to the actual line in the editor.
 */
const buildDeltaDecoration: DecorationFunc = (acc, { line, reason }) => {
  /*
   * NOTE: Sadly monaco only allows a direct className to render the decorations
   * You can find their definition on Editor.tsx SX property
   */
  return [
    ...acc,
    {
      range: buildLineRange(line),
      options: {
        glyphMarginClassName: 'monaco-glyph-error',
        glyphMarginHoverMessage: {
          value: reason,
        },
      },
    },
    {
      range: buildLineRange(line),
      options: {
        isWholeLine: true,
        linesDecorationsClassName: 'monaco-line-decoration',
        hoverMessage: {
          value: reason,
        },
      },
    },
    {
      range: buildInlineRange(line),
      options: { inlineClassName: 'monaco-inline-decoration' },
    },
  ];
};

/*
 * useEditorDecorations add line decoration to specific lines in the editor for visual feedback
 */
const useEditorDecorations = ({ editor, lineErrors, lineChanges }: UseEditorDecorationsProps) => {
  const activeDecorations = React.useRef([]);

  React.useEffect(() => {
    if (!editor) {
      return;
    }
    const decorations: DeltaDecoration[] = [];
    if (lineErrors?.length) {
      // Build editor line error decorations
      decorations.push(...lineErrors.reduce(buildDeltaDecoration, []));
    }
    if (lineChanges?.length) {
      lineChanges.forEach(
        ({ originalEndLineNumber, modifiedStartLineNumber, modifiedEndLineNumber }) => {
          // line was added
          if (originalEndLineNumber === 0) {
            decorations.push({
              range: buildLineRange(modifiedStartLineNumber, modifiedEndLineNumber),
              options: {
                isWholeLine: true,
                linesDecorationsClassName: 'monaco-added-line-decoration',
              },
            });
          }
          // line was removed
          else if (modifiedEndLineNumber === 0) {
            decorations.push({
              range: buildLineRange(modifiedStartLineNumber + 1),
              options: {
                isWholeLine: true,
                linesDecorationsClassName: 'monaco-removed-line-decoration',
              },
            });
          }
          // line modified
          else {
            decorations.push({
              range: buildLineRange(modifiedStartLineNumber, modifiedEndLineNumber),
              options: {
                isWholeLine: true,
                linesDecorationsClassName: 'monaco-modified-line-decoration',
              },
            });
          }
        },
      );
    }

    // Pass decorations to editor -- This approach will override existing decorations
    activeDecorations.current = editor.deltaDecorations(activeDecorations.current, decorations);
  }, [lineErrors, editor, lineChanges]);
};

export default useEditorDecorations;
